/**
 * Standardized Error Catalog for VFW2 Integration Partners
 *
 * This catalog defines a standardized set of error categories and error codes
 * exposed to integration partners. Each error includes:
 * - Code: Unique identifier for the error
 * - Category: High-level grouping of related errors
 * - Description: Human-readable explanation
 * - Recoverable: Whether the user can retry or fix the error
 */

/**
 * Error Categories (Numeric)
 * Format: 3-digit number for machine-readable error codes
 */
export enum ErrorCategory {
  /** System compatibility and configuration errors */
  SYSTEM_COMPATIBILITY = '001',
  /** Hardware device errors (microphone, speaker, camera) */
  HARDWARE_DEVICE = '002',
  /** Network connectivity and bandwidth errors */
  NETWORK_CONNECTIVITY = '003',
  /** Environment quality errors (noise, lighting) */
  ENVIRONMENT_QUALITY = '004',
  /** Server and API errors */
  SERVER_ERROR = '005',
  /** Test session and state management errors */
  SESSION_MANAGEMENT = '006',
  /** Test delivery and content errors */
  TEST_DELIVERY = '007',
  /** User authentication and authorization errors */
  AUTHENTICATION = '008',
}

/**
 * Error Category Names - Maps numeric codes to descriptive names
 */
export const ErrorCategoryNames: Record<ErrorCategory, string> = {
  [ErrorCategory.SYSTEM_COMPATIBILITY]: 'SYSTEM_COMPATIBILITY',
  [ErrorCategory.HARDWARE_DEVICE]: 'HARDWARE_DEVICE',
  [ErrorCategory.NETWORK_CONNECTIVITY]: 'NETWORK_CONNECTIVITY',
  [ErrorCategory.ENVIRONMENT_QUALITY]: 'ENVIRONMENT_QUALITY',
  [ErrorCategory.SERVER_ERROR]: 'SERVER_ERROR',
  [ErrorCategory.SESSION_MANAGEMENT]: 'SESSION_MANAGEMENT',
  [ErrorCategory.TEST_DELIVERY]: 'TEST_DELIVERY',
  [ErrorCategory.AUTHENTICATION]: 'AUTHENTICATION',
};

/**
 * Check ID mapping from internal error codes
 * Maps to the numeric values from utils.errorCode
 */
export const CheckId = {
  USE_API: '1',
  USE_CHECK_PROGRESS: '2',
  USE_PARSER: '3',
  BACKGROUND_NOISE_CHECK: '4',
  BANDWIDTH_CHECK: '5',
  SETUP_SUCCESS: '6',
  SYSTEM_CHECK: '7',
  TEST_WRAPPER_SERVER_ERROR: '8',
  TEST_WRAPPER_INITIALIZATION: '9',
  TEST_WRAPPER_RESUME: '10',
  TEST_WRAPPER_VIDEO_UID: '11',
  CONSENT_PAGE: '12',
  MICROPHONE_CHECK: '13',
  RECORD_VIDEO: '14',
  SETUP_SUMMARY: '15',
  SPEAKER_CHECK: '16',
  SPEAKING_TIPS: '17',
  TAKE_SELFIE: '18',
  SESSION_IS_ONGOING: '19',
  WAITING_PAGE: '20',
  TIN_RESERVATION: '21',
} as const;

/**
 * Check Names - Maps numeric IDs to descriptive names
 */
export const CheckNames: Record<string, string> = {
  [CheckId.USE_API]: 'useApi',
  [CheckId.USE_CHECK_PROGRESS]: 'useCheckProgress',
  [CheckId.USE_PARSER]: 'useParser',
  [CheckId.BACKGROUND_NOISE_CHECK]: 'backgroundNoiseCheck',
  [CheckId.BANDWIDTH_CHECK]: 'bandwidthCheck',
  [CheckId.SETUP_SUCCESS]: 'setupSuccess',
  [CheckId.SYSTEM_CHECK]: 'systemCheck',
  [CheckId.TEST_WRAPPER_SERVER_ERROR]: 'testWrapperServerError',
  [CheckId.TEST_WRAPPER_INITIALIZATION]: 'testWrapperInitialization',
  [CheckId.TEST_WRAPPER_RESUME]: 'testWrapperResume',
  [CheckId.TEST_WRAPPER_VIDEO_UID]: 'testWrapperVideoUID',
  [CheckId.CONSENT_PAGE]: 'consentPage',
  [CheckId.MICROPHONE_CHECK]: 'microphoneCheck',
  [CheckId.RECORD_VIDEO]: 'recordVideo',
  [CheckId.SETUP_SUMMARY]: 'setupSummary',
  [CheckId.SPEAKER_CHECK]: 'speakerCheck',
  [CheckId.SPEAKING_TIPS]: 'speakingTips',
  [CheckId.TAKE_SELFIE]: 'takeSelfie',
  [CheckId.SESSION_IS_ONGOING]: 'sessionIsOngoing',
  [CheckId.WAITING_PAGE]: 'waitingPage',
  [CheckId.TIN_RESERVATION]: 'tinReservation',
};

/**
 * Dynamic Error Code Builder
 * Constructs composite error codes from their component parts
 * Format: {checkId}_{categoryNumber}_{errorNumber}
 *
 * @param checkId - Check identifier (e.g., '7' for System Check)
 * @param category - Error category enum value (e.g., ErrorCategory.SYSTEM_COMPATIBILITY = '001')
 * @param errorNumber - Specific error number (e.g., '003')
 * @returns Composite error code (e.g., '7_001_003')
 *
 * @example
 * buildErrorCode('7', ErrorCategory.SYSTEM_COMPATIBILITY, '003')
 * // Returns: '7_001_003'
 *
 * buildErrorCode(CheckId.SYSTEM_CHECK, ErrorCategory.SYSTEM_COMPATIBILITY, '003')
 * // Returns: '7_001_003'
 */
export function buildErrorCode(
  checkId: string,
  category: ErrorCategory,
  errorNumber: string
): string {
  // Ensure errorNumber is zero-padded to 3 digits
  const paddedErrorNumber = errorNumber.padStart(3, '0');
  return `${checkId}_${category}_${paddedErrorNumber}`;
}
/**
 * Standardized Error Interface
 */
export interface StandardizedError {
  /** Unique error code for integration partners */
  code: string;
  /** Error category */
  category: ErrorCategory;
  /** Human-readable description */
  description: string;
  /** Whether the error is recoverable (user can retry) */
  recoverable: boolean;
  /** Internal error codes that map to this standardized error */
  internalCodes?: string[];
  /** Suggested actions for users */
  userAction?: string;
  /** Technical details for support teams */
  technicalDetails?: string;
}

/**
 * System Check Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 7 (SYSTEM_CHECK)
 * Category: 001 (SYSTEM_COMPATIBILITY) or 003 (NETWORK_CONNECTIVITY)
 *
 * Built dynamically using buildErrorCode()
 */
export const SystemCheckErrorCode = {
  /** Browser version is too old */
  BROWSER_VERSION_UNSUPPORTED: buildErrorCode(
    CheckId.SYSTEM_CHECK,
    ErrorCategory.SYSTEM_COMPATIBILITY,
    '001'
  ),
  /** Browser type is not supported */
  BROWSER_NOT_SUPPORTED: buildErrorCode(
    CheckId.SYSTEM_CHECK,
    ErrorCategory.SYSTEM_COMPATIBILITY,
    '002'
  ),
  /** Cookies are disabled */
  COOKIES_DISABLED: buildErrorCode(
    CheckId.SYSTEM_CHECK,
    ErrorCategory.SYSTEM_COMPATIBILITY,
    '003'
  ),
  /** Network connectivity test failed */
  NETWORK_TEST_FAILED: buildErrorCode(
    CheckId.SYSTEM_CHECK,
    ErrorCategory.NETWORK_CONNECTIVITY,
    '004'
  ),
  /** Generic system check failure */
  SYSTEM_CHECK_FAILED: buildErrorCode(
    CheckId.SYSTEM_CHECK,
    ErrorCategory.SYSTEM_COMPATIBILITY,
    '005'
  ),
} as const;

/**
 * Microphone Check Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 13 (MICROPHONE_CHECK)
 * Category: 002 (HARDWARE_DEVICE)
 *
 * Built dynamically using buildErrorCode()
 */
export const MicrophoneCheckErrorCode = {
  /** Microphone not detected */
  MICROPHONE_NOT_DETECTED: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '001'
  ),
  /** Microphone access denied */
  MICROPHONE_ACCESS_DENIED: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '002'
  ),
  /** Microphone audio level too low (maps to ERROR_WE_CANT_HEAR_YOU) */
  MICROPHONE_LEVEL_TOO_LOW: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '003'
  ),
  /** Microphone test failed */
  MICROPHONE_TEST_FAILED: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '004'
  ),
  /** Generic microphone check failure */
  MICROPHONE_CHECK_FAILED: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '005'
  ),
  /** No voice detected after sufficient volume samples (maps to ERROR) */
  MICROPHONE_NO_VOICE_DETECTED: buildErrorCode(
    CheckId.MICROPHONE_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '006'
  ),
} as const;

/**
 * Speaker Check Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 16 (SPEAKER_CHECK)
 * Category: 002 (HARDWARE_DEVICE)
 *
 * Built dynamically using buildErrorCode()
 */
export const SpeakerCheckErrorCode = {
  /** Speaker not detected */
  SPEAKER_NOT_DETECTED: buildErrorCode(
    CheckId.SPEAKER_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '001'
  ),
  /** Speaker audio not heard by user */
  SPEAKER_AUDIO_NOT_HEARD: buildErrorCode(
    CheckId.SPEAKER_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '002'
  ),
  /** Generic speaker check failure */
  SPEAKER_CHECK_FAILED: buildErrorCode(
    CheckId.SPEAKER_CHECK,
    ErrorCategory.HARDWARE_DEVICE,
    '003'
  ),
} as const;

/**
 * Bandwidth Check Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 5 (BANDWIDTH_CHECK)
 * Category: 003 (NETWORK_CONNECTIVITY)
 *
 * Built dynamically using buildErrorCode()
 */
export const BandwidthCheckErrorCode = {
  /** Download speed below minimum threshold */
  DOWNLOAD_SPEED_TOO_LOW: buildErrorCode(
    CheckId.BANDWIDTH_CHECK,
    ErrorCategory.NETWORK_CONNECTIVITY,
    '001'
  ),
  /** Network connection unstable */
  NETWORK_UNSTABLE: buildErrorCode(
    CheckId.BANDWIDTH_CHECK,
    ErrorCategory.NETWORK_CONNECTIVITY,
    '002'
  ),
  /** Generic bandwidth check failure */
  BANDWIDTH_CHECK_FAILED: buildErrorCode(
    CheckId.BANDWIDTH_CHECK,
    ErrorCategory.NETWORK_CONNECTIVITY,
    '003'
  ),
} as const;

/**
 * Background Noise Check Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 4 (BACKGROUND_NOISE_CHECK)
 * Category: 004 (ENVIRONMENT_QUALITY)
 *
 * Built dynamically using buildErrorCode()
 */
export const BackgroundNoiseCheckErrorCode = {
  /** Generic background noise check failure */
  NOISE_CHECK_FAILED: buildErrorCode(
    CheckId.BACKGROUND_NOISE_CHECK,
    ErrorCategory.ENVIRONMENT_QUALITY,
    '001'
  ),
} as const;

/**
 * Server Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * Category: 005 (SERVER_ERROR)
 * CheckId varies based on context
 *
 * Built dynamically using buildErrorCode()
 */
export const ServerErrorCode = {
  /** API request failed */
  API_REQUEST_FAILED: buildErrorCode(
    CheckId.USE_API,
    ErrorCategory.SERVER_ERROR,
    '001'
  ),
  /** Server internal error */
  SERVER_INTERNAL_ERROR: buildErrorCode(
    CheckId.TEST_WRAPPER_SERVER_ERROR,
    ErrorCategory.SERVER_ERROR,
    '002'
  ),
  /** Service unavailable */
  SERVICE_UNAVAILABLE: buildErrorCode(
    CheckId.TEST_WRAPPER_SERVER_ERROR,
    ErrorCategory.SERVER_ERROR,
    '003'
  ),
  /** Request timeout */
  REQUEST_TIMEOUT: buildErrorCode(
    CheckId.USE_API,
    ErrorCategory.SERVER_ERROR,
    '004'
  ),
} as const;

/**
 * Session Management Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * Category: 006 (SESSION_MANAGEMENT)
 *
 * Built dynamically using buildErrorCode()
 */
export const SessionErrorCode = {
  /** Session already in progress */
  SESSION_ALREADY_IN_PROGRESS: buildErrorCode(
    CheckId.SESSION_IS_ONGOING,
    ErrorCategory.SESSION_MANAGEMENT,
    '001'
  ),
  /** Session expired */
  SESSION_EXPIRED: buildErrorCode(
    CheckId.USE_CHECK_PROGRESS,
    ErrorCategory.SESSION_MANAGEMENT,
    '002'
  ),
  /** Invalid session state */
  INVALID_SESSION_STATE: buildErrorCode(
    CheckId.USE_CHECK_PROGRESS,
    ErrorCategory.SESSION_MANAGEMENT,
    '003'
  ),
  /** Session interrupted */
  SESSION_INTERRUPTED: buildErrorCode(
    CheckId.USE_CHECK_PROGRESS,
    ErrorCategory.SESSION_MANAGEMENT,
    '004'
  ),
} as const;

/**
 * Test Delivery Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * Category: 007 (TEST_DELIVERY)
 *
 * Built dynamically using buildErrorCode()
 */
export const TestDeliveryErrorCode = {
  /** Test initialization failed */
  TEST_INITIALIZATION_FAILED: buildErrorCode(
    CheckId.TEST_WRAPPER_INITIALIZATION,
    ErrorCategory.TEST_DELIVERY,
    '001'
  ),
  /** Test content loading failed */
  TEST_CONTENT_LOAD_FAILED: buildErrorCode(
    CheckId.USE_PARSER,
    ErrorCategory.TEST_DELIVERY,
    '002'
  ),
  /** Test resume failed */
  TEST_RESUME_FAILED: buildErrorCode(
    CheckId.TEST_WRAPPER_RESUME,
    ErrorCategory.TEST_DELIVERY,
    '003'
  ),
  /** Test submission failed */
  TEST_SUBMISSION_FAILED: buildErrorCode(
    CheckId.TEST_WRAPPER_SERVER_ERROR,
    ErrorCategory.TEST_DELIVERY,
    '004'
  ),
} as const;

/**
 * Authentication Error Codes
 * Format: {CheckId}_{Category}_{ErrorNumber}
 * CheckId: 21 (TIN_RESERVATION)
 * Category: 008 (AUTHENTICATION)
 *
 * Built dynamically using buildErrorCode()
 */
export const AuthenticationErrorCode = {
  /** TIN reservation failed */
  TIN_RESERVATION_FAILED: buildErrorCode(
    CheckId.TIN_RESERVATION,
    ErrorCategory.AUTHENTICATION,
    '001'
  ),
} as const;

/**
 * Comprehensive Error Catalog
 * Maps standardized error codes to detailed error information
 */
export const ERROR_CATALOG: Record<string, StandardizedError> = {
  // System Check Errors
  [SystemCheckErrorCode.BROWSER_VERSION_UNSUPPORTED]: {
    code: SystemCheckErrorCode.BROWSER_VERSION_UNSUPPORTED,
    category: ErrorCategory.SYSTEM_COMPATIBILITY,
    description:
      'Browser version is not supported. Minimum versions: Chrome 90, Firefox 111, Edge 100, Safari 15.',
    recoverable: false,
    internalCodes: ['7', 'systemCheck'],
    userAction:
      'Please update your browser to a supported version or use a different browser.',
    technicalDetails: 'Browser version check failed in SystemCheckWrapper',
  },
  [SystemCheckErrorCode.BROWSER_NOT_SUPPORTED]: {
    code: SystemCheckErrorCode.BROWSER_NOT_SUPPORTED,
    category: ErrorCategory.SYSTEM_COMPATIBILITY,
    description:
      'Browser type is not supported. Supported browsers: Chrome, Firefox, Edge, Safari.',
    recoverable: false,
    internalCodes: ['7', 'systemCheck'],
    userAction:
      'Please use one of the supported browsers: Chrome, Firefox, Edge, or Safari.',
    technicalDetails:
      'Browser compatibility check failed in SystemCheckWrapper',
  },
  [SystemCheckErrorCode.COOKIES_DISABLED]: {
    code: SystemCheckErrorCode.COOKIES_DISABLED,
    category: ErrorCategory.SYSTEM_COMPATIBILITY,
    description:
      'Browser cookies are disabled. Cookies are required for the test to function properly.',
    recoverable: true,
    internalCodes: ['7', 'systemCheck'],
    userAction: 'Please enable cookies in your browser settings and retry.',
    technicalDetails: 'navigator.cookieEnabled returned false',
  },
  [SystemCheckErrorCode.NETWORK_TEST_FAILED]: {
    code: SystemCheckErrorCode.NETWORK_TEST_FAILED,
    category: ErrorCategory.NETWORK_CONNECTIVITY,
    description:
      'Network connectivity test failed. Unable to reach test servers.',
    recoverable: true,
    internalCodes: ['7', 'systemCheck'],
    userAction:
      'Check your internet connection and retry. If the problem persists, contact your network administrator.',
    technicalDetails:
      'Network validation in SystemCheck failed to reach server',
  },
  [SystemCheckErrorCode.SYSTEM_CHECK_FAILED]: {
    code: SystemCheckErrorCode.SYSTEM_CHECK_FAILED,
    category: ErrorCategory.SYSTEM_COMPATIBILITY,
    description:
      'System check failed due to an unexpected error during system validation.',
    recoverable: true,
    internalCodes: ['7', 'systemCheck'],
    userAction:
      'Please retry the system check. If the problem persists, contact support.',
    technicalDetails: 'Generic system check error in pass() or proceed()',
  },

  // Microphone Check Errors
  [MicrophoneCheckErrorCode.MICROPHONE_NOT_DETECTED]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_NOT_DETECTED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description:
      'No microphone detected. Please connect a microphone to continue.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck'],
    userAction:
      'Connect a microphone and ensure it is properly configured in your system settings.',
    technicalDetails:
      'No audio input devices found during microphone enumeration',
  },
  [MicrophoneCheckErrorCode.MICROPHONE_ACCESS_DENIED]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_ACCESS_DENIED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description:
      'Microphone access denied. Browser permissions are required to use the microphone.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck'],
    userAction: 'Grant microphone permissions in your browser and retry.',
    technicalDetails: 'getUserMedia() permission denied',
  },
  [MicrophoneCheckErrorCode.MICROPHONE_LEVEL_TOO_LOW]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_LEVEL_TOO_LOW,
    category: ErrorCategory.HARDWARE_DEVICE,
    description:
      'Microphone audio level is too low. The system cannot detect sufficient audio input.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck'],
    userAction:
      'Adjust your microphone volume, move closer to the microphone, or check device settings.',
    technicalDetails:
      'Audio level below minimum threshold during microphone test',
  },
  [MicrophoneCheckErrorCode.MICROPHONE_TEST_FAILED]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_TEST_FAILED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description: 'Microphone test failed. Unable to record audio properly.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck'],
    userAction: 'Check your microphone connection and settings, then retry.',
    technicalDetails: 'CADE failure event triggered during microphone check',
  },
  [MicrophoneCheckErrorCode.MICROPHONE_CHECK_FAILED]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_CHECK_FAILED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description: 'Microphone check failed due to an unexpected error.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck'],
    userAction:
      'Retry the microphone check. If the problem persists, contact support.',
    technicalDetails: 'Generic microphone check error in pass() or proceed()',
  },
  [MicrophoneCheckErrorCode.MICROPHONE_NO_VOICE_DETECTED]: {
    code: MicrophoneCheckErrorCode.MICROPHONE_NO_VOICE_DETECTED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description:
      'No voice detected during microphone test. The microphone may not be picking up your voice.',
    recoverable: true,
    internalCodes: ['13', 'microphoneCheck', 'ERROR'],
    userAction:
      'Speak louder, move closer to the microphone, or check that your microphone is not muted.',
    technicalDetails:
      'Volume samples exceeded 200 without detecting sufficient voice activity',
  },

  // Speaker Check Errors
  [SpeakerCheckErrorCode.SPEAKER_NOT_DETECTED]: {
    code: SpeakerCheckErrorCode.SPEAKER_NOT_DETECTED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description:
      'No audio output device detected. Please connect speakers or headphones.',
    recoverable: true,
    internalCodes: ['16', 'speakerCheck'],
    userAction:
      'Connect speakers or headphones and ensure they are configured in your system settings.',
    technicalDetails:
      'No audio output devices found during speaker enumeration',
  },
  [SpeakerCheckErrorCode.SPEAKER_AUDIO_NOT_HEARD]: {
    code: SpeakerCheckErrorCode.SPEAKER_AUDIO_NOT_HEARD,
    category: ErrorCategory.HARDWARE_DEVICE,
    description: 'User indicated they cannot hear the test audio.',
    recoverable: true,
    internalCodes: ['16', 'speakerCheck'],
    userAction:
      'Check speaker volume, connection, and system audio settings. Try headphones if available.',
    technicalDetails: 'User selected "No" when asked if they could hear audio',
  },
  [SpeakerCheckErrorCode.SPEAKER_CHECK_FAILED]: {
    code: SpeakerCheckErrorCode.SPEAKER_CHECK_FAILED,
    category: ErrorCategory.HARDWARE_DEVICE,
    description: 'Speaker check failed due to an unexpected error.',
    recoverable: true,
    internalCodes: ['16', 'speakerCheck'],
    userAction:
      'Retry the speaker check. If the problem persists, contact support.',
    technicalDetails: 'Generic speaker check error',
  },

  // Bandwidth Check Errors
  [BandwidthCheckErrorCode.DOWNLOAD_SPEED_TOO_LOW]: {
    code: BandwidthCheckErrorCode.DOWNLOAD_SPEED_TOO_LOW,
    category: ErrorCategory.NETWORK_CONNECTIVITY,
    description: 'Download speed is below the minimum requirement.',
    recoverable: true,
    internalCodes: ['5', 'bandwidthCheck'],
    userAction:
      'Close other applications using bandwidth, move closer to your router, or contact your ISP.',
    technicalDetails: 'Download speed test failed minimum threshold check',
  },
  [BandwidthCheckErrorCode.NETWORK_UNSTABLE]: {
    code: BandwidthCheckErrorCode.NETWORK_UNSTABLE,
    category: ErrorCategory.NETWORK_CONNECTIVITY,
    description: 'Network connection is unstable or unreliable.',
    recoverable: true,
    internalCodes: ['5', 'bandwidthCheck'],
    userAction:
      'Ensure stable internet connection. Use wired connection if possible.',
    technicalDetails: 'Network stability check failed',
  },
  [BandwidthCheckErrorCode.BANDWIDTH_CHECK_FAILED]: {
    code: BandwidthCheckErrorCode.BANDWIDTH_CHECK_FAILED,
    category: ErrorCategory.NETWORK_CONNECTIVITY,
    description: 'Bandwidth check failed due to an unexpected error.',
    recoverable: true,
    internalCodes: ['5', 'bandwidthCheck'],
    userAction:
      'Retry the bandwidth check. If the problem persists, contact support.',
    technicalDetails: 'Generic bandwidth check error in pass() or proceed()',
  },

  // Background Noise Check Errors
  [BackgroundNoiseCheckErrorCode.NOISE_CHECK_FAILED]: {
    code: BackgroundNoiseCheckErrorCode.NOISE_CHECK_FAILED,
    category: ErrorCategory.ENVIRONMENT_QUALITY,
    description: 'Background noise check failed due to an unexpected error.',
    recoverable: true,
    internalCodes: ['4', 'backgroundNoiseCheck'],
    userAction:
      'Retry the noise check. If the problem persists, contact support.',
    technicalDetails:
      'Generic background noise check error in pass() or proceed()',
  },

  // Server Errors
  [ServerErrorCode.API_REQUEST_FAILED]: {
    code: ServerErrorCode.API_REQUEST_FAILED,
    category: ErrorCategory.SERVER_ERROR,
    description: 'API request to the server failed.',
    recoverable: true,
    internalCodes: ['1', 'useApi'],
    userAction:
      'Retry the operation. If the problem persists, contact support.',
    technicalDetails: 'HTTP request failed or returned error status',
  },
  [ServerErrorCode.SERVER_INTERNAL_ERROR]: {
    code: ServerErrorCode.SERVER_INTERNAL_ERROR,
    category: ErrorCategory.SERVER_ERROR,
    description:
      'Server encountered an internal error while processing the request.',
    recoverable: true,
    internalCodes: ['8', 'testWrapperServerError'],
    userAction:
      'Wait a few moments and retry. If the problem persists, contact support.',
    technicalDetails: 'Server returned 500 series error',
  },
  [ServerErrorCode.SERVICE_UNAVAILABLE]: {
    code: ServerErrorCode.SERVICE_UNAVAILABLE,
    category: ErrorCategory.SERVER_ERROR,
    description: 'Test service is temporarily unavailable.',
    recoverable: true,
    internalCodes: ['8', 'testWrapperServerError'],
    userAction: 'Service is temporarily down. Please try again later.',
    technicalDetails: 'Server returned 503 Service Unavailable',
  },
  [ServerErrorCode.REQUEST_TIMEOUT]: {
    code: ServerErrorCode.REQUEST_TIMEOUT,
    category: ErrorCategory.SERVER_ERROR,
    description: 'Request to server timed out.',
    recoverable: true,
    internalCodes: ['1', 'useApi'],
    userAction: 'Check your internet connection and retry.',
    technicalDetails: 'Request exceeded timeout threshold',
  },

  // Session Management Errors
  [SessionErrorCode.SESSION_ALREADY_IN_PROGRESS]: {
    code: SessionErrorCode.SESSION_ALREADY_IN_PROGRESS,
    category: ErrorCategory.SESSION_MANAGEMENT,
    description: 'A test session is already in progress.',
    recoverable: false,
    internalCodes: ['19', 'sessionIsOngoing'],
    userAction:
      'Complete or exit the existing session before starting a new one.',
    technicalDetails: 'Detected existing active session',
  },
  [SessionErrorCode.SESSION_EXPIRED]: {
    code: SessionErrorCode.SESSION_EXPIRED,
    category: ErrorCategory.SESSION_MANAGEMENT,
    description: 'Test session has expired.',
    recoverable: false,
    internalCodes: [],
    userAction: 'Your session has expired. Please start a new test session.',
    technicalDetails: 'Session timeout exceeded',
  },
  [SessionErrorCode.INVALID_SESSION_STATE]: {
    code: SessionErrorCode.INVALID_SESSION_STATE,
    category: ErrorCategory.SESSION_MANAGEMENT,
    description: 'Session is in an invalid state.',
    recoverable: false,
    internalCodes: ['2', 'useCheckProgress'],
    userAction: 'Please restart the test from the beginning.',
    technicalDetails: 'Session state validation failed',
  },
  [SessionErrorCode.SESSION_INTERRUPTED]: {
    code: SessionErrorCode.SESSION_INTERRUPTED,
    category: ErrorCategory.SESSION_MANAGEMENT,
    description: 'Test session was interrupted.',
    recoverable: true,
    internalCodes: [],
    userAction: 'Resume the test from where you left off.',
    technicalDetails: 'Session interruption detected',
  },

  // Test Delivery Errors
  [TestDeliveryErrorCode.TEST_INITIALIZATION_FAILED]: {
    code: TestDeliveryErrorCode.TEST_INITIALIZATION_FAILED,
    category: ErrorCategory.TEST_DELIVERY,
    description: 'Failed to initialize the test.',
    recoverable: true,
    internalCodes: ['9', 'testWrapperInitialization'],
    userAction:
      'Retry test initialization. If the problem persists, contact support.',
    technicalDetails: 'Test initialization process failed',
  },
  [TestDeliveryErrorCode.TEST_CONTENT_LOAD_FAILED]: {
    code: TestDeliveryErrorCode.TEST_CONTENT_LOAD_FAILED,
    category: ErrorCategory.TEST_DELIVERY,
    description: 'Failed to load test content.',
    recoverable: true,
    internalCodes: ['3', 'useParser'],
    userAction: 'Check your internet connection and retry.',
    technicalDetails: 'Test content parsing or loading failed',
  },
  [TestDeliveryErrorCode.TEST_RESUME_FAILED]: {
    code: TestDeliveryErrorCode.TEST_RESUME_FAILED,
    category: ErrorCategory.TEST_DELIVERY,
    description: 'Failed to resume the test from saved progress.',
    recoverable: true,
    internalCodes: ['10', 'testWrapperResume'],
    userAction:
      'Retry resuming the test. If the problem persists, contact support.',
    technicalDetails: 'Test resume process failed',
  },
  [TestDeliveryErrorCode.TEST_SUBMISSION_FAILED]: {
    code: TestDeliveryErrorCode.TEST_SUBMISSION_FAILED,
    category: ErrorCategory.TEST_DELIVERY,
    description: 'Failed to submit test responses.',
    recoverable: true,
    internalCodes: [],
    userAction: 'Ensure stable internet connection and retry submission.',
    technicalDetails: 'Test submission request failed',
  },

  // Authentication Errors
  [AuthenticationErrorCode.TIN_RESERVATION_FAILED]: {
    code: AuthenticationErrorCode.TIN_RESERVATION_FAILED,
    category: ErrorCategory.AUTHENTICATION,
    description:
      'TIN reservation failed. Unable to start the test session with the provided TIN.',
    recoverable: false,
    internalCodes: ['21', 'tinReservation'],
    userAction:
      'Please verify your TIN is correct and contact support if the problem persists.',
    technicalDetails: 'TIN reservation API call failed during direct launch',
  },
};

/**
 * Maps internal error codes to standardized error codes
 */
export const INTERNAL_TO_STANDARD_ERROR_MAP: Record<string, string> = {
  '1': ServerErrorCode.API_REQUEST_FAILED,
  '2': SessionErrorCode.INVALID_SESSION_STATE,
  '3': TestDeliveryErrorCode.TEST_CONTENT_LOAD_FAILED,
  '4': BackgroundNoiseCheckErrorCode.NOISE_CHECK_FAILED,
  '5': BandwidthCheckErrorCode.BANDWIDTH_CHECK_FAILED,
  '6': ServerErrorCode.SERVER_INTERNAL_ERROR, // setupSuccess
  '7': SystemCheckErrorCode.SYSTEM_CHECK_FAILED,
  '8': ServerErrorCode.SERVER_INTERNAL_ERROR,
  '9': TestDeliveryErrorCode.TEST_INITIALIZATION_FAILED,
  '10': TestDeliveryErrorCode.TEST_RESUME_FAILED,
  '11': ServerErrorCode.SERVER_INTERNAL_ERROR, // testWrapperVideoUID
  '12': SessionErrorCode.INVALID_SESSION_STATE, // consentPage
  '13': MicrophoneCheckErrorCode.MICROPHONE_CHECK_FAILED,
  '14': ServerErrorCode.SERVER_INTERNAL_ERROR, // recordVideo
  '15': ServerErrorCode.SERVER_INTERNAL_ERROR, // setupSummary
  '16': SpeakerCheckErrorCode.SPEAKER_CHECK_FAILED,
  '17': ServerErrorCode.SERVER_INTERNAL_ERROR, // speakingTips
  '18': ServerErrorCode.SERVER_INTERNAL_ERROR, // takeSelfie
  '19': SessionErrorCode.SESSION_ALREADY_IN_PROGRESS,
  '20': ServerErrorCode.SERVER_INTERNAL_ERROR, // waitingPage
  '21': AuthenticationErrorCode.TIN_RESERVATION_FAILED, // tinReservation
  useApi: ServerErrorCode.API_REQUEST_FAILED,
  useCheckProgress: SessionErrorCode.INVALID_SESSION_STATE,
  useParser: TestDeliveryErrorCode.TEST_CONTENT_LOAD_FAILED,
  backgroundNoiseCheck: BackgroundNoiseCheckErrorCode.NOISE_CHECK_FAILED,
  bandwidthCheck: BandwidthCheckErrorCode.BANDWIDTH_CHECK_FAILED,
  systemCheck: SystemCheckErrorCode.SYSTEM_CHECK_FAILED,
  microphoneCheck: MicrophoneCheckErrorCode.MICROPHONE_CHECK_FAILED,
  speakerCheck: SpeakerCheckErrorCode.SPEAKER_CHECK_FAILED,
};

/**
 * Get standardized error information from internal error code
 */
export function getStandardizedError(
  internalCode: string
): StandardizedError | null {
  const standardCode = INTERNAL_TO_STANDARD_ERROR_MAP[internalCode];
  if (standardCode && ERROR_CATALOG[standardCode]) {
    return ERROR_CATALOG[standardCode];
  }
  return null;
}

/**
 * Get all errors by category
 */
export function getErrorsByCategory(
  category: ErrorCategory
): StandardizedError[] {
  return Object.values(ERROR_CATALOG).filter(
    (error) => error.category === category
  );
}

/**
 * Check if an error is recoverable
 */
export function isErrorRecoverable(errorCode: string): boolean {
  const error = ERROR_CATALOG[errorCode];
  return error?.recoverable ?? true;
}

/**
 * Get user-facing error message
 */
export function getUserErrorMessage(errorCode: string): string {
  const error = ERROR_CATALOG[errorCode];
  if (!error) {
    return 'An unexpected error occurred. Please contact support.';
  }
  return `${error.description} ${error.userAction || ''}`;
}
